/**
 * 海报相关类型定义
 ***************************************************************************************
 */
/**
 * 播放相关类型定义
 * Mark角标
 */
enum MarkLabelPosition {
  LowerLeft = 0,
  LowerRight = 1,
  UpperRight = 2,
  UpperLeft = 3,
  LowerCenter = 4,
}
enum MarkLabelType {
  MarkLabelPay = 0,
}
enum LoadType {
  Manual = 0, // 手工下拉刷新
  Timeout = 1, // 超时刷新（没有则不传）
  Preload = 2, // 打开页面刷新
  NextPage = 3, // 翻页刷新
}
interface MarkLabel {
  text: string;
  position: MarkLabelPosition;
  feature_color: string;
  url: string;
  height: number;
  width: number;
}
interface MarkLabelInfo {
  type: MarkLabelType; // 角标类型
  mark_label: MarkLabel; // 角标
}
/**
 * 播放相关类型定义
 * Action，上报以及跳转【点击行为】
 */
interface ReportData {
  report_key: string;
  report_params: string;
}
interface Action {
  url: string;
  report_data?: ReportData;
}

/**
 * 播放相关类型定义
 * TagLabel，标签展示
 */
interface TagLabel {
  text?: string;
  text_color?: string;
  bg_color?: string;
  filter_choice?: Record<string, string>;
  icon_url?: string;
}

/**
 * 播放相关类型定义
 * ActorInfo，演员信息
 */
interface VoteInfo {
  support_count: string; // 当前电力值
  actor_id: string; // 助力明星的actorid
  group_id: string; // 助力明星的用户组
  action: string; // 点击跳转action
  button_content: string; // 按钮文本
}
interface ActorInfo {
  actor_name: string;
  actor_id: string;
  face_image_url?: string;
  action?: Action | null;
  vote_info?: VoteInfo | null;
}

/**
 * 播放相关类型定义
 * Poster信息
 */
interface PosterExInfo {
  /** 纯净图裁剪图 16:9 、9:16 */
  pure_img_op_url?: string;
  /** 老图 （横图、竖图） */
  img_url?: string;
}
interface PlayCalender {
  url: string; // 跳转地址
  img_url: string; // 日历图片
}
interface Poster {
  /** Main title */
  main_title?: string;
  /** Subtitle */
  subtitle?: string;
  /** Description text */
  description?: string;
  /** Poster image url */
  img_url?: string;
  /** Mark labels */
  mark_label_list?: MarkLabel[];
  /** Report params */
  report_data?: ReportData;
  /** Jump action */
  action?: Action;
  /** Ad position key */
  ad_key?: string;
  /** Poster id */
  poster_id?: string;
  /** Multi-country updated episode count */
  episode_updated_country?: number;
  /** Tag labels */
  tag_label_list?: TagLabel[];
  cid?: string;
  pid?: string;
  vid?: string;
  /** Main color extracted from image */
  img_main_color?: string;
  tid?: string;
  /** Actors */
  actor_list?: ActorInfo[];
  /** Tid content type: "1" collection; "2" playlist */
  tid_type?: string;
  /** Internal url */
  url?: string;
  /** External url */
  url_outer?: string;
  /** Expose limit, 0 means unlimited */
  expose_limit?: number;
  bg_img_url?: string;
  logo_img_url?: string;
  title_hide?: boolean;
  nid?: string;
  area_name?: string;
  off_shelf_status?: boolean;
  mapping_vid?: string;
  tab_name?: string;
  channel_id?: string;
  channel_type?: number;
  play_count?: string;
  art_title_img?: string;
  horizontal_info?: PosterExInfo;
  vertical_info?: PosterExInfo;
  episode_updated_text?: string;
}

/**
 * 剧集相关类型定义
 ***************************************************************************************
 */
/**
 * 目前用于 播放详情页面ui展示样式
 */
enum PlayListUiType {
  PlayListTypeText = 0,
  PlayListTypePoster = 1,
  PlayListTypeVerticalPoster = 2,
}
enum PlayerModel {
  PlayerModelLong = 0, // 精品库类型专辑
  PlayerModelSkit = 1, // 短剧类型专辑
}
interface PopularizationInfo {
  title: string;
  tag: string; // 右上角标签
  action: Action; // 跳转链接
  horizontal_poster: Poster; // 横版海报，续播提示时展示
  vertical_poster: Poster; // 竖版海报，播放列表展示
}
/**
 * 某一季的数据
 */
interface SeasonInfo {
  title: string;
  cid: string;
  next_page_info: NextPageInfo;
  play_calender: PlayCalender;
  play_list_ui_type: PlayListUiType;
  detail_info: string; // 详细信息文本
  player_model: PlayerModel; // 播放模式
  popularization_info: PopularizationInfo; // 推广位信息
  publish_year: number; // 制片年份
  update_episode: number; // 视频更新到第几集
  full_episode_count: number; // 视频一共有多少集
  poster: Poster; // 海报信息
}

/**
 * 某一集vid相关类型定义
 ***************************************************************************************
 */
/**
 * 影片付费类型
 */
enum VideoPaymentType {
  Unknown = 0,
  Vod = 4, // 单片点播 （会员用券，非会员支持单点，用券）
  VodVipExmt = 5, // 单片点播 & 会员豁免 （会员免费看，非会员支持单点，用券）
  VipOnly = 6, // 仅会员可看 （会员专享，非会员必须开通会员）
  FreeForAll = 8, // 全部都免费
  SinglePay = 7, // 单片付费
  SinglePayPlus = 9, // 单片付费PLUS
  VodAhead = 12, // 超前点播
}
/**
 * 播放版权限制类型
 */
enum VideoCopyrightType {
  PlayInWebview = 0, // 表示要跳webview播放
  PlayInApp = 1, // 直接播放
  PlayInWebviewWithStmt = 101, // 跳webView播放并显示"应版权方要求不再app播放"提示
}
/**
 * ShareItem 分享
 */
interface ShareItem {
  share_url: string; // 分享url
  share_title: string; // 分享标题
  share_subtitle: string; // 分享副标题 （实际用于分享携带信息）
  share_img_url: string; // 分享图片
  vid: string; // VID
  cid: string; // CID
  share_vertical_img_url: string; // 竖图
  share_square_img_url: string; // 方图
  pid: string; // PID
  display_message: string; // 分享时携带的文案，展示为副标题
}
/**
 * VideoItemData 当前播视频数据
 */
interface VideoItemData {
  vid: string; // 视频id     ［如果是外站数据，请按照1，2，3 这样往后编号］
  payment_type: VideoPaymentType; // 影片付费类型（原：play_status）
  poster: Poster; // 用来负责ui显示 ［如果视频有海报图，均从这里取］
  skip_start: number; // 跳过片头时间（秒）
  skip_end: number; // 跳过片尾时间（秒）
  action: Action; // 点击当前剧集后执行的动作
  title: string; // 全屏显示的标题
  play_copyright_type: VideoCopyrightType; // 播放版本限制类型（原: play_copy_right）
  watch_record_poster: Poster; // 观看历史使用的视图信息
  share_data: ShareItem; // 分享item
  next_play_key: string; // 下一个播放的专辑
  try_watch_duration: number; // 试看时长单位：秒
  cid: string; // 视频对应cid
  pay_preview: boolean; // 付费预告片标识,若值为true,则会员用户需隐藏该vid
  stream_ratio: number; // 视频流宽高比
  not_record_history: boolean; // 指定该video不记录历史，默认是false
  disable_external_play: boolean; // 关闭投射功能，默认false
  episode_id: number; // 视频是第几集
  is_drm: boolean; // 是否是DRM视频
  publish_time: string; // 视频发布时间（秒）
  play_count: string; // 播放量
  duration: string; // 播放时长
  vid_type: number; // vid类型 1=长视频 2=短视频
  status: number; // 视频状态
  auto_play_type: number; // 自动播放类型：0.不自动播放；1.wifi自动播放；2.wifi和移动网络都自动播放
  age_limit: number; // 年龄限制 0就是不限制
  episode_id_text: string; // 显示使用的集数
  first_frame_poster: string; // 第一帧画面
  skip_head_begin: number; // 跳过片头的开始时间(秒),skip_start当作片头的结束时间点
  skip_tail_end: number; // 片尾的结束时间(秒), skip_end当作片尾的开始时间点
  is_trailer: boolean; // 是否是预告片
}

/**
 * 历史记录相关类型定义
 ***************************************************************************************
 */
/**
 * 历史记录视频类型
 */
enum VideoType {
  VTUNKNOWN = 0,
  LONG = 1, // 长视频
  SHORT = 2, // 短视频
  LIVE = 3, // 直播
}

// IDType id类型
enum IDType {
  RecordTypeVid = 0, // 0.VID
  RecordTypeCid = 1, // 1.CID
  RecordTypePid = 2, // 2.PID
}

// OpType 操作类型
enum OpType {
  OpTypeInit = 0, // 0.初始值
  OpTypeUpdate = 1, // 1.更新
  OpTypeAdd = 2, // 2.添加
  OpTypeDel = 3, // 3.删除
}

enum Scene {
  DefaultScene = 0, // 默认观看历史列表场景
  AutoplayScene = 1, // 自动播场景
}

/**
 * 历史记录
 */
// TV封面数据
export interface TVCoverData {
  cover_id: string; // cid
  desc: string; // 简介
  title: string; // 标题
  type: number; // 专辑类型
  ott_tags: MarkLabel[]; // TV角标
  square_tags: MarkLabel[]; // TV方标
  episode_updated: string; // 更新集数
  imgtag_ver: string; // 统一角标
  publish_date: string; // 上线日期
  second_title: string; // 副标题
  art_word: string; // 艺术字
  pic_url_vt: string; // 竖海报，普通图
  pure_pic_url_vt: string; // 竖海报，净版图
  pic_url_hz: string; // 横海报，普通图
  pure_pic_url_hz: string; // 横海报，净版图
}

interface HistoryRecord {
  vid: string; // 单视频id
  cid: string; // 专辑id
  pid: string; // 直播id
  modify_time: string; // 最后修改时间（历史产生/更新/删除的时间）utc（毫秒）
  video_play_time: number; // 影片已经播放的时间，单位秒
  video_total_time: number; // 视频总时长，单位秒
  type: VideoType; // 视频类型
  primary_category: number; // 视频一级分类
  full_episode_count: number; // 这个cid的总集数
  update_episode: number; // 这个cid最新更新的集数
  update_time: string; // 这个cid最新更新的时间（秒）
  episode: number; // 这个vid的集数
  publish_time: string; // 这个vid的发布时间（秒）
  pay_status: number; // 媒资付费状态
  cover_title: string;
  cover_type: number; // 媒资专辑类型
  update_episode_multinational: string; // 这个cid最新更新的集数,优先2B形式，数字形式兜底
  watch_time: number; // 用户观看时长
}

/**
 * 看单相关类型定义
 ***************************************************************************************
 */
/**
 * 看单
 */
// 看单
interface WatchList {
  is_show: boolean; // 看单功能是否开启,默认关闭
  is_watched: boolean; // 是否已加入看单
}

/**
 * 分页相关类型定义
 ***************************************************************************************
 */
/**
 * 下一页信息
 */
interface NextPageInfo {
  data_key: string;
  page_context: string;
  has_next_page: boolean;
}

/**
 * 实验相关类型定义
 ***************************************************************************************
 */
/**
 * 实验信息
 */
interface ExperimentInfo {
  experiment_id: string;
}

export {
  // Poster信息
  MarkLabelPosition,
  MarkLabelType,
  LoadType,
  MarkLabel,
  MarkLabelInfo,
  ReportData,
  Action,
  TagLabel,
  VoteInfo,
  ActorInfo,
  PosterExInfo,
  PlayCalender,
  Poster,
  // 剧集相关类型定义
  PlayListUiType,
  PlayerModel,
  PopularizationInfo,
  SeasonInfo,
  // 某一集vid相关类型定义
  VideoPaymentType,
  VideoCopyrightType,
  ShareItem,
  VideoItemData,
  // 历史记录相关类型定义
  VideoType,
  IDType,
  OpType,
  Scene,
  HistoryRecord,
  // 看单
  WatchList,
  // 下一页信息
  NextPageInfo,
  // 实验信息
  ExperimentInfo,
};
